import { extrairMensagemErroApi } from "../utils/mensagemErroApi";
import type { UsuarioSessao } from "../types/auth";

/**
 * Cliente RPC do back-end Node/Express usado pelo React.
 *
 * O back-end expoe: POST /api/rpc/:name com body { args: [...] }
 * e responde JSON. A autenticacao usa sessao por cookie.
 */

const RPC_BASE = "/api/rpc";
let contextoRpc = "";
let revisaoContextoRpc = 0;

/** Respostas iniciadas por outro login ou empresa não podem preencher a sessão atual. */
export function definirContextoRpc(usuario: UsuarioSessao | null) {
  const novoContexto = usuario ? JSON.stringify([
    String(usuario.codLogin ?? ""), String(usuario.selectedCodOrg ?? usuario.codOrg ?? ""), String(usuario.codEmpresa ?? ""),
  ]) : "";
  if (novoContexto === contextoRpc) return;
  contextoRpc = novoContexto;
  revisaoContextoRpc += 1;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

/** Erro lançado quando a sessão expirou / usuário não autenticado (HTTP 401). */
export class NaoAutenticadoError extends ApiError {
  constructor(payload?: unknown) {
    super("Usuário não autenticado.", 401, payload);
    this.name = "NaoAutenticadoError";
  }
}

export async function callRpc<T = unknown>(
  name: string,
  ...args: unknown[]
): Promise<T> {
  const contextoDaChamada = contextoRpc;
  const revisaoDaChamada = revisaoContextoRpc;
  let res: Response;
  try {
    res = await fetch(`${RPC_BASE}/${encodeURIComponent(name)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-I9-Tela": window.location.pathname,
        ...(contextoDaChamada ? { "X-I9-Contexto": contextoDaChamada } : {}),
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({ args }),
    });
  } catch (networkError) {
    throw new ApiError(
      "Falha de conexão com o servidor.",
      0,
      networkError
    );
  }

  const raw = await res.text();
  if (revisaoDaChamada !== revisaoContextoRpc) {
    throw new ApiError("O login ou a empresa mudou durante a consulta. Atualize a tela.", 409);
  }
  let data: unknown = null;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = raw;
    }
  }

  if (res.status === 401) {
    throw new NaoAutenticadoError(data);
  }
  if (!res.ok) {
    const msg = extrairMensagemErroApi(data, "Não foi possível concluir a operação no servidor.");
    throw new ApiError(msg, res.status, data);
  }

  return data as T;
}
